/* ============================================================================
   The Rare Steps — ProductCardQuickAdd controller (ONE delegated script)
   Powers snippets/trs-quick-add.liquid on every product card.

   Browse-first interaction:
     • Pointer  → hover/focus reveals the frosted overlay (pure CSS). Clicking a
                  size adds it to the bag instantly — no second click.
     • Touch    → a floating "+" opens a single shared bottom sheet of sizes.
                  Tapping a size adds it and the sheet dismisses itself.

   - Fully event-delegated → works for cards injected later (load-more, AJAX
     filtering, recommendations, quick-view) with no re-init.
   - Adds via /cart/add.js and refreshes Dawn's cart drawer + count bubble.
   - Keyboard accessible (arrow keys move between chips; ESC closes; focus
     reveals the overlay). Honours prefers-reduced-motion via CSS.
   ========================================================================== */
(function () {
  if (window.__trsQuickAddInit) return;
  window.__trsQuickAddInit = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var LS_KEY = 'trs:lastSize';
  var RESET_MS = 1700;

  var sheet = null; // the single shared bottom sheet (lazy)

  function lastSize() { try { return localStorage.getItem(LS_KEY); } catch (e) { return null; } }
  function rememberSize(v) { try { if (v) localStorage.setItem(LS_KEY, v); } catch (e) {} }

  /* The owning .trs-qa for any interactive element (handles sheet clones). */
  function qaOf(el) {
    var inSheet = el.closest('.trs-qa-sheet');
    if (inSheet && inSheet.__qa) return inSheet.__qa;
    return el.closest('[data-trs-qa]');
  }

  /* ---- Add to cart --------------------------------------------------------- */
  function addToBag(qa, id, fromSheet) {
    if (!qa || !id || qa.getAttribute('data-state') === 'adding') return;
    qa.setAttribute('data-state', 'adding');
    if (fromSheet && sheet) sheet.classList.add('is-busy');

    var drawer = document.querySelector('cart-drawer');
    var body = { items: [{ id: Number(id), quantity: 1 }] };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      } catch (e) { /* no sections */ }
    }

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error((r.data && r.data.description) || 'Add to cart failed');
        qa.setAttribute('data-state', 'added');

        if (fromSheet) showSheetSuccess();

        if (drawer && r.data.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(r.data);
        } else {
          updateBubble();
        }

        // Keep third-party listeners / analytics working.
        document.dispatchEvent(new CustomEvent('trs:cart:added', { bubbles: true, detail: { id: id } }));

        if (fromSheet) {
          window.setTimeout(closeSheet, 850);
        } else {
          // Desktop: the overlay bows out even while the pointer is still on the
          // card, then re-arms cleanly once the pointer leaves.
          qa.classList.add('is-dismissed');
          armDismissClear(qa);
          if (drawer && typeof drawer.open === 'function') {
            window.setTimeout(function () { drawer.open(); }, 260);
          }
        }

        // After the success beat, drop back to idle (toast fades) but stay
        // dismissed on desktop until the pointer leaves.
        window.setTimeout(function () { settleQa(qa); }, RESET_MS);
      })
      .catch(function () {
        resetQa(qa);
        if (sheet) sheet.classList.remove('is-busy');
        qa.classList.add('trs-qa--error');
        window.setTimeout(function () { qa.classList.remove('trs-qa--error'); }, 1200);
      });
  }

  /* Return the control to its resting visual state, keeping any desktop
     "dismissed" flag so a just-added overlay doesn't pop back under the cursor. */
  function settleQa(qa) {
    qa.setAttribute('data-state', 'idle');
    qa.querySelectorAll('[data-trs-qa-chip].is-selected').forEach(function (c) {
      c.classList.remove('is-selected');
    });
  }

  /* Full reset (used on error). */
  function resetQa(qa) {
    settleQa(qa);
    qa.classList.remove('is-dismissed');
  }

  /* Once the pointer leaves the card, re-arm the hover overlay. */
  function armDismissClear(qa) {
    if (qa.__dismissArmed) return;
    qa.__dismissArmed = true;
    var clear = function () {
      qa.classList.remove('is-dismissed');
      qa.__dismissArmed = false;
      qa.removeEventListener('mouseleave', clear);
    };
    qa.addEventListener('mouseleave', clear);
  }

  function updateBubble() {
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  /* ---- Mobile bottom sheet ------------------------------------------------- */
  function buildSheet() {
    if (sheet) return sheet;
    var backdrop = document.createElement('div');
    backdrop.className = 'trs-qa-sheet__backdrop';
    backdrop.setAttribute('data-trs-qa-backdrop', '');

    sheet = document.createElement('div');
    sheet.className = 'trs-qa-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Choose a size');
    sheet.innerHTML =
      '<div class="trs-qa-sheet__grip"></div>' +
      '<div class="trs-qa-sheet__head">' +
        '<div><span class="trs-qa-sheet__sub">Quick Add</span>' +
        '<span class="trs-qa-sheet__title" data-sheet-title>Choose a size</span></div>' +
        '<button type="button" class="trs-qa-sheet__close" data-trs-qa-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="trs-qa-sheet__sizes" data-sheet-sizes></div>';

    document.body.appendChild(backdrop);
    document.body.appendChild(sheet);
    sheet.__backdrop = backdrop;
    return sheet;
  }

  function openSheet(qa) {
    buildSheet();
    sheet.__qa = qa;
    sheet.classList.remove('is-busy');
    qa.querySelector('[data-trs-qa-fab]')?.blur();

    var title = sheet.querySelector('[data-sheet-title]');
    if (title) title.textContent = qa.getAttribute('data-title') || 'Choose a size';

    var holder = sheet.querySelector('[data-sheet-sizes]');
    holder.innerHTML = '';
    var src = qa.querySelector('[data-trs-qa-sizes]');
    if (src) {
      src.querySelectorAll('[data-trs-qa-chip]').forEach(function (chip) {
        holder.appendChild(chip.cloneNode(true));
      });
    }

    document.documentElement.style.overflow = 'hidden';
    sheet.__backdrop.classList.add('is-open');
    requestAnimationFrame(function () { sheet.classList.add('is-open'); });

    var fab = qa.querySelector('[data-trs-qa-fab]');
    if (fab) fab.setAttribute('aria-expanded', 'true');

    var first = holder.querySelector('[data-trs-qa-chip]:not([disabled])');
    if (first) window.setTimeout(function () { first.focus(); }, 320);
  }

  function showSheetSuccess() {
    if (!sheet) return;
    var holder = sheet.querySelector('[data-sheet-sizes]');
    if (holder) {
      holder.innerHTML =
        '<div class="trs-qa-sheet__done">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>' +
          '<span>Added to bag</span>' +
        '</div>';
    }
  }

  function closeSheet() {
    if (!sheet || !sheet.classList.contains('is-open')) return;
    sheet.classList.remove('is-open');
    sheet.__backdrop.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    var qa = sheet.__qa;
    if (qa) {
      var fab = qa.querySelector('[data-trs-qa-fab]');
      if (fab) fab.setAttribute('aria-expanded', 'false');
    }
  }

  /* ---- Delegated clicks ---------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var backdrop = e.target.closest('[data-trs-qa-backdrop]');
    if (backdrop) { closeSheet(); return; }

    var close = e.target.closest('[data-trs-qa-close]');
    if (close) { e.preventDefault(); closeSheet(); return; }

    // Direct add (single-variant CTA, or single-variant "+" on touch).
    var add = e.target.closest('[data-trs-qa-add]');
    if (add) {
      e.preventDefault();
      addToBag(qaOf(add), add.getAttribute('data-variant-id'), !!add.closest('.trs-qa-sheet'));
      return;
    }

    // A size chip — add that exact variant immediately.
    var chip = e.target.closest('[data-trs-qa-chip]');
    if (chip) {
      if (chip.disabled || chip.getAttribute('aria-disabled') === 'true') return;
      chip.classList.add('is-selected');
      rememberSize(chip.getAttribute('data-value'));
      addToBag(qaOf(chip), chip.getAttribute('data-variant-id'), !!chip.closest('.trs-qa-sheet'));
      return;
    }

    // The floating "+" in size mode → open the bottom sheet.
    var fab = e.target.closest('[data-trs-qa-fab]');
    if (fab && !fab.hasAttribute('data-trs-qa-add')) {
      e.preventDefault();
      openSheet(qaOf(fab));
      return;
    }
  });

  /* ---- Keyboard ------------------------------------------------------------ */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet && sheet.classList.contains('is-open')) {
      closeSheet();
      return;
    }
    var chip = e.target.closest && e.target.closest('[data-trs-qa-chip]');
    if (!chip) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var container = chip.closest('[data-trs-qa-sizes], [data-sheet-sizes]') || chip.parentNode;
    var chips = Array.prototype.slice.call(container.querySelectorAll('[data-trs-qa-chip]:not([disabled])'));
    var i = chips.indexOf(chip);
    if (i === -1) return;
    e.preventDefault();
    var next = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? chips[i + 1] || chips[0] : chips[i - 1] || chips[chips.length - 1];
    if (next) next.focus();
  });
})();
